import React from 'react';
import { Link } from 'react-router-dom';

/** Bump when replacing client/public/sellerclub-logo.png */
const LOGO_SRC = '/sellerclub-logo.png?v=7';

const MARKETING_SIZES = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-14',
  xl: 'h-16',
};

export default function Logo({
  className = '',
  size = 'md',
  variant = 'marketing',
  isDarkMode = false,
  linkToDashboard = true,
}) {
  const imageClass = variant === 'compact'
    ? 'h-6 w-auto max-w-[112px] object-contain object-left'
    : variant === 'header'
    ? 'h-7 w-auto max-w-[148px] object-contain object-left'
    : variant === 'sidebar'
      ? 'h-8 w-full max-w-[200px] object-contain object-left'
      : `${MARKETING_SIZES[size] || MARKETING_SIZES.md} w-auto max-w-[280px] object-contain`;

  const image = (
    <img
      src={LOGO_SRC}
      alt="SellerClub"
      draggable={false}
      className={`select-none ${imageClass}`}
    />
  );

  if (variant === 'compact') {
    const linkClass =
      'inline-flex items-center shrink-0 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 rounded-sm';
    const plateClass =
      'logo-plate keep-light inline-flex items-center rounded-md bg-white px-2 py-1 shadow-sm ring-1 ring-slate-200/80';
    const inner = linkToDashboard ? (
      <Link to="/dashboard" className={linkClass} aria-label="SellerClub home">
        {image}
      </Link>
    ) : (
      <div className={linkClass}>{image}</div>
    );
    return <div className={`${plateClass} ${className}`}>{inner}</div>;
  }

  if (variant === 'header') {
    const linkClass =
      'inline-flex items-center shrink-0 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 rounded-sm';
    // Dark header: solid white plate so black “SELLER” text in the PNG stays readable (prop + `dark` class).
    const plateClass =
      'logo-plate keep-light inline-flex items-center rounded-lg bg-white px-3 py-1.5 shadow-sm ring-1 ring-slate-200/90';
    const inner = linkToDashboard ? (
      <Link to="/dashboard" className={linkClass} aria-label="SellerClub home">
        {image}
      </Link>
    ) : (
      <div className={linkClass}>{image}</div>
    );
    const darkPlate =
      'dark:logo-plate dark:keep-light dark:inline-flex dark:items-center dark:rounded-lg dark:bg-white dark:px-3 dark:py-1.5 dark:shadow-sm dark:ring-1 dark:ring-slate-200/90';
    return (
      <div className={`${isDarkMode ? plateClass : `contents ${darkPlate}`} ${className}`}>
        {inner}
      </div>
    );
  }

  if (variant === 'sidebar') {
    // Brand asset is black + orange on white — always show on a light strip (dark sidebar included).
    return (
      <div
        className={`flex-shrink-0 w-full border-b ${
          isDarkMode ? 'border-slate-700/80' : 'border-slate-200'
        } logo-plate keep-light bg-white ${className}`}
      >
        <div className="px-3 py-4 min-h-[64px] flex items-center">
          {linkToDashboard ? (
            <Link
              to="/dashboard"
              className="inline-flex w-full transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 rounded-sm"
              aria-label="SellerClub home"
            >
              {image}
            </Link>
          ) : (
            image
          )}
        </div>
      </div>
    );
  }

  const marketingPlate =
    'logo-plate keep-light inline-flex items-center rounded-xl bg-white px-4 py-2.5 shadow-md ring-1 ring-slate-200/80';

  const darkMarketingPlate =
    'dark:logo-plate dark:keep-light dark:inline-flex dark:items-center dark:rounded-xl dark:bg-white dark:px-4 dark:py-2.5 dark:shadow-md dark:ring-1 dark:ring-slate-200/80';
  return (
    <div className={`flex items-center ${className}`}>
      <div className={isDarkMode ? marketingPlate : `contents ${darkMarketingPlate}`}>{image}</div>
    </div>
  );
}
